import classnames from 'classnames';
import type {ReactNode} from 'react';
import React, {useState} from 'react';
import {createPortal} from 'react-dom';

import AnnotationEditor from './AnnotationEditor';
import {useAnnotatableContentPresence, useAnnotationModeStore} from './AnnotationModeStore';
import {buildSupplementAnchor} from './annotationAnchors';
import {isShieldExempt} from './annotationFreeze';
import {buildSupplementRegionId} from './annotationRegions';
import {ANNOTATE_IGNORE_PROPS} from './annotationTextProjection';
import type {HighlightColor} from './annotationTypes';
import {annotationAnchorAtPoint} from './annotationUtils';
import {SUPPLEMENT_REGION_CLASS} from './supplementCard';
import {useAnnotationGesture} from './useAnnotationGesture';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

// Figma paints a 4px border on the supplement at its own radius. The card owns that
// border (annotations-highlights.scss); the wrapper only names the color, which the
// card inherits. Hover is state, not a `hover:` variant, so the color lands as a
// plain utility Tailwind always emits.
const BORDER_COLOR_BY_MARK: Record<HighlightColor, string> = {
  yellow: '[--annotation-supplement-border:var(--color-background-highlight-yellow)]',
  blue: '[--annotation-supplement-border:var(--color-background-highlight-blue)]',
  pink: '[--annotation-supplement-border:var(--color-background-highlight-pink)]'
};

const HOVER_BORDER_COLOR = '[--annotation-supplement-border:var(--color-border-selected)]';

/** The types that take a whole-supplement mark; passage and sortable-table stay text surfaces. */
export const ANNOTATABLE_SUPPLEMENT_TYPES: ReadonlySet<string> = new Set(['image', 'audio']);

interface AnnotatableSupplementProps {
  supplementId: string;
  supplementType: string;
  /** Hosts whose supplements must not take marks (answer chips, ordering items). */
  disabled?: boolean;
  children: ReactNode;
}

/**
 * Wraps a supplement so a click in annotation mode attaches a whole-supplement
 * mark (part-3, #18667): at most one per supplement, painted as a colored
 * outline, edited through the shared AnnotationEditor. The subtree is fenced
 * from the host region's text projection — the supplement itself is the target,
 * and its region is supplement-scoped so the mark carries across questions.
 */
export default function AnnotatableSupplement({
  supplementId,
  supplementType,
  disabled = false,
  children
}: AnnotatableSupplementProps) {
  // Static checks first, hookless: a chip's or passage's supplement must not
  // even subscribe to the availability gate (its flag query needs Apollo).
  if (disabled || !ANNOTATABLE_SUPPLEMENT_TYPES.has(supplementType)) {
    return <>{children}</>;
  }

  return (
    <AvailableAnnotatableSupplement supplementId={supplementId}>
      {children}
    </AvailableAnnotatableSupplement>
  );
}

function AvailableAnnotatableSupplement({
  supplementId,
  children
}: Pick<AnnotatableSupplementProps, 'supplementId' | 'children'>) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();

  if (!isAnnotationModeAvailable) {
    return <>{children}</>;
  }

  return (
    <AnnotatableSupplementTarget supplementId={supplementId}>
      {children}
    </AnnotatableSupplementTarget>
  );
}

function AnnotatableSupplementTarget({
  supplementId,
  children
}: Pick<AnnotatableSupplementProps, 'supplementId' | 'children'>) {
  const regionId = buildSupplementRegionId(supplementId);
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  // At most one mark per supplement: the head of the bucket is the mark.
  const mark = useAnnotationModeStore(
    (state) => state.annotationsByRegionId[regionId]?.[0] ?? null
  );
  const [isHovered, setIsHovered] = useState(false);
  const {activeAnnotation, handleMouseUp, closeEditor} = useAnnotationGesture({
    // Exempt supplement controls (zoom, expand, play) act; they don't mark.
    shouldYield: (event) => event.button !== 0 || isShieldExempt(event),
    // At most one mark per supplement: a click reopens it, or creates the first.
    resolveAtPoint: (event) =>
      annotationAnchorAtPoint(
        event.clientX,
        event.clientY,
        mark?.id ?? addAnnotation(buildSupplementAnchor(supplementId))
      ),
    consume: true
  });
  useAnnotatableContentPresence(true);

  return (
    <>
      <div
        {...ANNOTATE_IGNORE_PROPS}
        data-testid='annotatable-supplement'
        className={classnames(
          SUPPLEMENT_REGION_CLASS,
          mark && BORDER_COLOR_BY_MARK[mark.color],
          isAnnotationModeOn && 'cursor-highlighter',
          isAnnotationModeOn && !mark && isHovered && HOVER_BORDER_COLOR
        )}
        onMouseUp={handleMouseUp}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </div>
      {activeAnnotation &&
        // Portaled to body: mounting even the popover's fixed anchor span inside
        // the host region's container would churn its MutationObserver and flash
        // every text mark on open/close.
        createPortal(
          <AnnotationEditor
            regionId={regionId}
            activeAnnotation={activeAnnotation}
            onClose={closeEditor}
            allowUnderline={false}
          />,
          document.body
        )}
    </>
  );
}
